import { CombinedReports } from "../features/reports/reportsSlice";
import { CombinedNoteParams } from "../features/reports/reportTypes";
import { formatAddress } from "./formatAddress";
import { formatPhoneNumber } from "./formatPhoneNumber";
import { fullCapsText } from "./fullCapsText";
import { generateList } from "./generateList";
import {
  generateNeighborhoodReportHighlights,
  generatePeopleReportHighlights,
  generatePhoneReportHighlights,
  generatePropertyReportHighlights,
  generateSocialReportHighlights,
  generateVehicleReportHighlights,
} from "./generateReportHighlights";
import { renderSocialReportType } from "./renderSocialReportType";

export const addNoteToReportParamBuilder = (
  report: CombinedReports,
  note: string,
): CombinedNoteParams | undefined => {
  if (report) {
    switch (report?.reportType) {
      case "people": {
        return {
          note: note,
          reportType: "people",
          displayName: report.aliases ? report.aliases[0].fullName : "",
          reportDetails: generatePeopleReportHighlights(report),
          reportParams: {
            reportToken: report.reportToken ? report.reportToken : "",
          },
        };

        break;
      }
      case "phone": {
        return {
          note: note,
          displayName: formatPhoneNumber(report.searchCriteria.phone),
          reportDetails: generatePhoneReportHighlights(report, false) as string,
          reportType: "phone",
          reportParams: {
            phone: report?.searchCriteria.phone,
          },
        };
        break;
      }
      case "social": {
        if (
          report.socialReportSummary.email ||
          report.socialReportSummary.phone ||
          report.socialReportSummary.twitter
        ) {
          const displayName = generateList([
            {
              value: report.socialReportSummary.email,
              label: "",
            },
            {
              value: report.socialReportSummary.phone,
              label: "",
            },
            {
              value: report.socialReportSummary.twitter,
              label: "",
            },
          ]);
          return {
            note: note,
            displayName: fullCapsText(displayName),
            reportDetails: generateSocialReportHighlights(
              report,
              renderSocialReportType(report.socialReportSummary),
            ),
            reportType: "social",
            reportParams: {
              socialToken: displayName,
            },
          };
        }
        break;
      }
      case "property": {
        if (report.propertyDetails) {
          return {
            note: note,
            displayName: fullCapsText(
              formatAddress(report.propertyDetails, "with-postalCode"),
            ),
            reportDetails: generatePropertyReportHighlights(report),
            reportType: "property",
            reportParams: {
              addressLine1: report.propertyDetails.addressLine1
                ? report.propertyDetails.addressLine1
                : "",
              addressLine2: report.propertyDetails.addressLine2,
              city: report.propertyDetails.city
                ? report.propertyDetails.city
                : "",
              state: report.propertyDetails.state
                ? report.propertyDetails.state
                : "",
              zip: report.propertyDetails.postalCode
                ? report.propertyDetails.postalCode
                : "",
            },
          };
        }
        break;
      }
      case "neighborhood": {
        if (report.addressObject) {
          return {
            note: note,
            displayName: fullCapsText(report.addressSearch),
            reportDetails: generateNeighborhoodReportHighlights(report),
            reportType: "neighborhood",
            reportParams: {
              //May need to update to allow for empty addressLine1 or zip
              addressLine1: report.addressObject.address
                ? report.addressObject.address
                : "",
              addressLine2: report.addressObject.addressLine2
                ? report.addressObject.addressLine2
                : "",
              city: report.addressObject.city ? report.addressObject.city : "",
              state: report.addressObject.state
                ? report.addressObject.state
                : "",
              zip: report.addressObject.zipCode
                ? report.addressObject.zipCode
                : undefined,
            },
          };
        }
        break;
      }
      case "vehicle": {
        if (report.vehicleDetails) {
          return {
            note: note,
            displayName: report.vehicleSummary.vehicleDescription
              ? fullCapsText(report.vehicleSummary.vehicleDescription)
              : "",
            reportDetails: generateVehicleReportHighlights(report),
            reportType: "auto",
            reportParams: {
              vin: report.vehicleSummary.vin,
            },
          };
        }
        break;
      }
    }
  }
};

export const getSpecificReportNotesParamBuilder = (
  report: CombinedReports,
): CombinedNoteParams | undefined => {
  if (report) {
    switch (report.reportType) {
      case "people": {
        return {
          reportType: "people",
          reportParams: {
            reportToken: report.reportToken ? report.reportToken : "",
          },
        };

        break;
      }
      case "phone": {
        return {
          reportType: "phone",
          reportParams: {
            phone: report.searchCriteria.phone,
          },
        };
        break;
      }
      case "social": {
        if (
          report.socialReportSummary.email ||
          report.socialReportSummary.phone ||
          report.socialReportSummary.twitter
        ) {
          const displayName = generateList([
            {
              value: report.socialReportSummary.email,
              label: "",
            },
            {
              value: report.socialReportSummary.phone,
              label: "",
            },
            {
              value: report.socialReportSummary.twitter,
              label: "",
            },
          ]);
          return {
            reportType: "social",
            reportParams: {
              socialToken: displayName,
            },
          };
        }
        break;
      }
      case "property": {
        if (report.propertyDetails) {
          return {
            reportType: "property",
            reportParams: {
              addressLine1: report.propertyDetails.addressLine1
                ? report.propertyDetails.addressLine1
                : "",
              addressLine2: report.propertyDetails.addressLine2,
              city: report.propertyDetails.city
                ? report.propertyDetails.city
                : "",
              state: report.propertyDetails.state
                ? report.propertyDetails.state
                : "",
              zip: report.propertyDetails.postalCode
                ? report.propertyDetails.postalCode
                : "",
            },
          };
        }
        break;
      }
      case "neighborhood": {
        if (report.addressObject) {
          return {
            reportType: "neighborhood",
            reportParams: {
              addressLine1: report.addressObject.address
                ? report.addressObject.address
                : "",
              addressLine2: report.addressObject.addressLine2
                ? report.addressObject.addressLine2
                : "",
              city: report.addressObject.city ? report.addressObject.city : "",
              state: report.addressObject.state
                ? report.addressObject.state
                : "",
              zip: report.addressObject.zipCode
                ? report.addressObject.zipCode
                : undefined,
            },
          };
        }
        break;
      }
      case "vehicle": {
        if (report.vehicleDetails) {
          return {
            reportType: "auto",
            reportParams: {
              vin: report.vehicleSummary.vin,
            },
          };
        }
        break;
      }
    }
  }
};
